import React, { useState } from 'react';
import { ScanFace, ArrowRight, Droplets, Sparkles, Heart, Camera, CheckCircle, X } from 'lucide-react';

const ScanPage = () => {
  const [step, setStep] = useState('select'); // select, scanning, results

  const scanOptions = [
    { id: 'pimples', label: 'Pimples', icon: <Sparkles size={16} /> },
    { id: 'darkSpots', label: 'Dark Spots', icon: <Droplets size={16} /> },
    { id: 'eyeBags', label: 'Eye Bags', icon: <Heart size={16} /> },
    { id: 'texture', label: 'Texture', icon: <Sparkles size={16} /> },
    { id: 'redness', label: 'Redness', icon: <Droplets size={16} /> },
    { id: 'pores', label: 'Pores', icon: <Camera size={16} /> },
  ];

  const [selectedConcerns, setSelectedConcerns] = useState([]);

  const toggleConcern = (id) => {
    if (selectedConcerns.includes(id)) {
      setSelectedConcerns(selectedConcerns.filter(item => item !== id));
    } else {
      setSelectedConcerns([...selectedConcerns, id]);
    }
  };

  const handleScan = () => {
    if (selectedConcerns.length > 0) {
      setStep('scanning');
      setTimeout(() => {
        setStep('results');
      }, 2500);
    }
  };

  const handleReset = () => {
    setStep('select');
    setSelectedConcerns([]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">AI Skin Analysis</h1>
        <p className="text-gray-500 mt-1">Get personalized skincare recommendations</p>
      </div>

      {step === 'select' && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-6 md:p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
              <ScanFace size={32} className="text-[#EFA9B6]" />
            </div>
            <h2 className="text-xl font-serif font-bold text-gray-900">Select Your Concerns</h2>
            <p className="text-sm text-gray-500 mt-1">Choose the areas you'd like the AI to analyze</p>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-8">
            {scanOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => toggleConcern(option.id)}
                className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedConcerns.includes(option.id)
                    ? 'bg-[#FFF5F6] border-[#EFA9B6] text-[#EFA9B6]'
                    : 'bg-white border-gray-100 text-gray-600 hover:border-[#EFA9B6] hover:text-[#EFA9B6]'
                }`}
              >
                {option.icon}
                {option.label}
                {selectedConcerns.includes(option.id) && <CheckCircle size={14} />}
              </button>
            ))}
          </div>

          <button
            onClick={handleScan}
            disabled={selectedConcerns.length === 0}
            className={`w-full py-3.5 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              selectedConcerns.length > 0
                ? 'bg-[#010101] text-white hover:bg-black/90'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ScanFace size={18} />
            Start Analysis
            <ArrowRight size={16} />
          </button>

          <p className="text-center text-xs text-gray-400 mt-4">
            Select at least one concern to begin the analysis
          </p>
        </div>
      )}

      {step === 'scanning' && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-6 md:p-8 text-center">
          <div className="relative w-32 h-32 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-[#FFF5F6] rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#EFA9B6] rounded-full animate-spin border-t-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <ScanFace size={40} className="text-[#EFA9B6] animate-pulse" />
            </div>
          </div>
          <h2 className="text-xl font-serif font-bold text-gray-900 mb-2">Scanning Your Skin</h2>
          <p className="text-sm text-gray-500">Analyzing subdermal layers and texture maps...</p>
          <div className="mt-6 flex justify-center gap-2">
            <div className="w-2 h-2 bg-[#EFA9B6] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#EFA9B6] rounded-full animate-bounce delay-100"></div>
            <div className="w-2 h-2 bg-[#EFA9B6] rounded-full animate-bounce delay-200"></div>
          </div>
        </div>
      )}

      {step === 'results' && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-xs font-bold text-[#EFA9B6] uppercase tracking-widest mb-1">Your Skin Looks</div>
              <h2 className="text-2xl font-serif font-bold text-gray-900">Healthy & Radiant</h2>
            </div>
            <div className="w-20 h-20 rounded-full border-[6px] border-[#FFF5F6] bg-white flex flex-col items-center justify-center relative">
              <svg className="absolute inset-0 w-full h-full -rotate-90">
                <circle cx="50%" cy="50%" r="32" fill="none" stroke="#EFA9B6" strokeWidth="6" strokeDasharray="201" strokeDashoffset="30" />
              </svg>
              <span className="font-bold text-gray-900 text-xl">85%</span>
              <span className="text-[8px] font-bold text-gray-400 uppercase mt-0.5">Excellent</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Hydration', value: '72%', status: 'Good', color: 'text-[#EFA9B6]' },
              { label: 'Texture', value: '85%', status: 'Refined', color: 'text-blue-500' },
              { label: 'Pores', value: '76%', status: 'High', color: 'text-amber-500' },
              { label: 'Redness', value: '15%', status: 'Low', color: 'text-green-500' },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#FCF8F9] rounded-2xl p-3 text-center border border-gray-50">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">{stat.label}</div>
                <div className={`font-bold text-lg ${stat.color}`}>{stat.value}</div>
                <div className="text-[10px] font-bold text-green-500 uppercase">{stat.status}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setStep('select')}
              className="flex-1 bg-[#010101] text-white py-3.5 rounded-full font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
            >
              <ScanFace size={18} />
              Scan Again
            </button>
            <button
              onClick={handleReset}
              className="flex-1 bg-gray-50 text-gray-600 py-3.5 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <X size={18} />
              Close
            </button>
          </div>

          <div className="mt-4 p-4 bg-[#FFF5F6] rounded-2xl">
            <p className="text-sm text-gray-700 font-medium">💡 Recommended: Try our Vitamin C Serum for enhanced glow and brightness.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanPage;